import DOMPurify from 'dompurify'
import { useCallback, useEffect, useState } from 'react'

type CheckResult = { result: 'safe' } | { result: 'unsafe'; error: Error }
type CheckElementFunction = (element: Element) => CheckResult

type UseSafeHtmlReturnType = 'safe' | 'unsafe'

/**
 *
 * @description 特定の構造のformタグのみHTMLレンダーを許容するための判定機能
 */
export const useSafeHtml = ({
  text,
}: {
  text: string
}): UseSafeHtmlReturnType => {
  const checker = useIsValidElement()
  const [results, setResults] = useState<CheckResult[]>([])
  useEffect(() => {
    const clean = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['form', 'input', 'button'],
      ALLOWED_ATTR: ['type', 'value', 'action', 'method', 'name'],
      RETURN_DOM: true,
    })

    // sanitize後、取り除かれた要素が2つ以上の場合、optionで許容したタグ・属性以外のものが含まれていたと判断し、unsafeとする
    // ※必ずbodyタグが取り除かれるため、1つは許容する
    if (DOMPurify.removed.length > 1) {
      setResults([
        {
          result: 'unsafe',
          error: new Error('two or more elements removed'),
        },
      ])
      return
    }
    // さらに、取り除かれた要素が1つかつその要素がbodyでなかった場合にもunsafeとする
    if (
      DOMPurify.removed.length === 1 &&
      DOMPurify.removed[0].element.nodeName !== 'BODY'
    ) {
      setResults([
        {
          result: 'unsafe',
          error: new Error('removed element was not "body"'),
        },
      ])
      return
    }
    // 最上位がFormタグのみかをチェックする
    const topLevelNodes = clean.childNodes
    topLevelNodes.forEach(node => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        setResults([
          {
            result: 'unsafe',
            error: new Error('unexpected element'),
          },
        ])
        return
      }
      // NOTE HTML要素である前提で進める
      const element = node as HTMLElement
      if (element.tagName !== 'FORM') {
        setResults([
          {
            result: 'unsafe',
            error: new Error('unexpected element'),
          },
        ])
      }
    })

    // Sanitize後のDOMの各要素に対して、適切な属性のみ持っているかの検査を行う
    const elements = clean.querySelectorAll('*')
    elements.forEach(node => {
      switch (node.tagName) {
        case 'FORM':
          setResults(prev => [...prev, checker.isValidForm(node)])
          break
        case 'INPUT':
          setResults(prev => [...prev, checker.isValidInput(node)])
          break
        case 'BUTTON':
          setResults(prev => [...prev, checker.isValidButton(node)])
          break
        default:
          setResults(prev => [
            ...prev,
            {
              result: 'unsafe',
              error: new Error('unexpected element'),
            },
          ])
          break
      }
    })
  }, [])
  // NOTE 以上の判定を経て、一つでも条件に一致しない箇所があればunsafeとして判定する
  return results?.some(r => r.result === 'unsafe') ? 'unsafe' : 'safe'
}

export const useIsValidElement = () => {
  const isValidForm = useCallback<CheckElementFunction>(element => {
    if (element.hasAttribute('type')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have type`),
      }
    }
    if (element.hasAttribute('value')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have value`),
      }
    }
    if (element.hasAttribute('name')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have name`),
      }
    }
    if (!element.hasAttribute('action')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must have action`),
      }
    }
    const childNodes = element.childNodes
    if (childNodes.length === 0) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have childNode`),
      }
    }

    // Form内部の要素が、1つ以上のinputと1つのbuttonと0つ以上のTextNodeのみであることを確認する
    const hasNonInputOrNonButtonOrNonTextNode = Array.from(childNodes).some(
      node => {
        switch (node.nodeType) {
          case Node.TEXT_NODE:
            return false
          case Node.ELEMENT_NODE: {
            const elm = node as HTMLElement
            return elm.tagName !== 'INPUT' && elm.tagName !== 'BUTTON'
          }
          default:
            return true
        }
      }
    )
    if (hasNonInputOrNonButtonOrNonTextNode) {
      return {
        result: 'unsafe',
        error: new Error(
          `${element.nodeName} must not have non button or non input element`
        ),
      }
    }
    // NOTE buttonが2つ以上あることを許容しない
    const hasMultipleButtons =
      Array.from(childNodes)
        .filter(node => node.nodeType === Node.ELEMENT_NODE)
        .filter(node => {
          const elm = node as HTMLElement
          return elm.tagName === 'BUTTON'
        }).length > 1
    if (hasMultipleButtons)
      return {
        result: 'unsafe',
        error: new Error(
          `${element.nodeName} must not have two or more buttons`
        ),
      }

    return { result: 'safe' }
  }, [])

  const isValidInput = useCallback<CheckElementFunction>(element => {
    if (
      !element.hasAttribute('type') ||
      (element.getAttribute('type') !== 'hidden' &&
        element.getAttribute('type') !== 'submit')
    ) {
      return {
        result: 'unsafe',
        error: new Error(
          `${element.nodeName} must have type, and that must be 'hidden' or 'submit'`
        ),
      }
    }
    if (!element.hasAttribute('value')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must have value`),
      }
    }
    if (element.hasAttribute('action')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have action`),
      }
    }
    const childNodes = element.childNodes
    if (childNodes.length > 0) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have childNode`),
      }
    }
    return { result: 'safe' }
  }, [])

  const isValidButton = useCallback<CheckElementFunction>(element => {
    if (
      !element.hasAttribute('type') ||
      element.getAttribute('type') !== 'submit'
    ) {
      return {
        result: 'unsafe',
        error: new Error(
          `${element.nodeName} must have type, and that must be 'submit'`
        ),
      }
    }
    if (element.hasAttribute('value')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have value`),
      }
    }
    if (element.hasAttribute('action')) {
      return {
        result: 'unsafe',
        error: new Error(`${element.nodeName} must not have action`),
      }
    }

    const childNodes = element.childNodes
    if (
      !childNodes ||
      childNodes.length === 0 ||
      childNodes.length > 1 ||
      childNodes[0].nodeName !== '#text'
    ) {
      return {
        result: 'unsafe',
        error: new Error(
          `${element.nodeName} must have just one childNode, and that must be a text node`
        ),
      }
    }
    return { result: 'safe' }
  }, [])
  return {
    isValidForm,
    isValidInput,
    isValidButton,
  }
}
